import { Check } from "@tamagui/lucide-icons";
import type React from "react";
import type { PropsWithChildren } from "react";
import {
  type GetThemeValueForKey,
  Popover,
  type PopoverProps,
  useWindowDimensions,
  View,
  XStack,
  YStack,
} from "tamagui";
import { Body } from "./Body";
import { Label } from "./Label";

type PopoverConfirmProps = {
  onConfirm: () => void;
  confirmTitle?: JSX.Element | string;
  confirmDescription?: JSX.Element | string;
  skip?: boolean;
  type?: "danger" | "success" | "warning";
};

export const PopoverConfirm: React.FC<
  PropsWithChildren<PopoverProps & PopoverConfirmProps>
> = ({
  children,
  onConfirm,
  confirmDescription,
  confirmTitle,
  skip,
  type = "danger",
  ...props
}) => {
  const dimensions = useWindowDimensions();
  const maxWidth = Math.min(500, dimensions.width - 20);

  if (skip) {
    return (
      <View onPress={onConfirm} cursor="pointer">
        {children}
      </View>
    );
  }

  let buttonColor: GetThemeValueForKey<"color"> = "$danger";
  let buttonTextColor: GetThemeValueForKey<"color"> = "#fff";

  if (type === "success") {
    buttonColor = "#d0ffbc";
    buttonTextColor = "$color";
  } else if (type === "warning") {
    buttonColor = "$primary";
    buttonTextColor = "$color";
  }

  return (
    <Popover size="$size.medium" allowFlip placement="left" {...props}>
      <Popover.Trigger onPress={(e) => e.stopPropagation()} cursor="pointer">
        {children}
      </Popover.Trigger>

      <Popover.Content
        borderWidth={1}
        borderColor="$divider"
        enterStyle={{ y: -10, opacity: 0 }}
        exitStyle={{ y: -10, opacity: 0 }}
        padding={0}
        borderRadius={0}
        backgroundColor={"$backgroundBlock"}
        shadowColor={"#000"}
        shadowOpacity={0.15}
        shadowRadius={24}
        shadowOffset={{ height: 0, width: 0 }}
        onPress={(e) => e.stopPropagation()}
        maxWidth={maxWidth}
        animation={[
          "quick",
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
      >
        <Popover.Arrow
          backgroundColor={"$backgroundBlock"}
          borderWidth={1}
          borderColor="$divider"
        />
        <YStack gap="$space.medium" padding="$space.medium">
          <Label>{confirmTitle ?? "Are you sure you want to delete?"}</Label>
          <Body>{confirmDescription ?? "This can't be undone"}</Body>
        </YStack>
        <Popover.Close asChild>
          <XStack
            borderTopWidth={1}
            borderColor={"$divider"}
            width="100%"
            alignItems="center"
            cursor="pointer"
          >
            <XStack
              padding="$space.medium"
              justifyContent="center"
              flex={1}
              alignItems="center"
              gap="$small"
              backgroundColor={buttonColor}
              onPress={onConfirm}
            >
              <Check color={buttonTextColor} size="$small" />
              <Label color={buttonTextColor}>Confirm</Label>
            </XStack>
            <View padding="$space.medium" flex={1}>
              <Body textAlign="center">Cancel</Body>
            </View>
          </XStack>
        </Popover.Close>
      </Popover.Content>
    </Popover>
  );
};
